import { createStore } from 'vuex';
import products from './products';
import {
  loadCartFromStorage,
  persistCart,
  clearCartStorage,
  generateLineId,
  CART_STORAGE_KEY,
} from '@/utils/cartStorage';

const applyCartChange = (currentCart, mutator) => {
  const updated = mutator(currentCart.map((item) => ({ ...item })));
  persistCart(updated);
  return updated;
};

export default createStore({
  modules: {
    products
  },
  state: {
    cart: loadCartFromStorage(),
    auth: {
      token: localStorage.getItem('accessToken') || null,
      username: localStorage.getItem('username') || '',
      isAdmin: localStorage.getItem('isAdmin') === 'true',
      userId: localStorage.getItem('userId') || null,
      userEmail: localStorage.getItem('userEmail') || '',
    },
  },
  getters: {
    cartCount: (state) => {
      return state.cart.reduce((total, item) => total + item.quantity, 0);
    },
    cartTotal: (state) => {
      return state.cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    cartItems: (state) => state.cart,
    isCartOpen: (state) => state.isCartOpen,

    // Auth getters
    isAuthenticated: (state) => !!state.auth.token,
    currentUsername: (state) => state.auth.username,
    isAdmin: (state) => state.auth.isAdmin,
    userId: (state) => state.auth.userId,
    userEmail: (state) => state.auth.userEmail,
  },
  mutations: {
    SET_CART(state, cart) {
      state.cart = cart;
    },
    ADD_TO_CART(state, item) {
      state.cart = applyCartChange(state.cart, (cart) => {
        const lineId = generateLineId(item);
        const existingItem = cart.find(i => i.lineId === lineId);
        if (existingItem) {
          existingItem.quantity += item.quantity || 1;
          return cart;
        }
        return [...cart, {
          id: item.id,
          blister_id: item.blister_id,
          name: item.name,
          price: item.price,
          original_price: item.original_price,
          brand: item.brand,
          image: item.image,
          category_id: item.category_id,
          quantity: item.quantity || 1,
          currency: item.currency || 'ARS',
          discount: item.discount,
          total_discount: item.total_discount || item.discount || 0,
          variants: item.variants || {},
          has_special_promotion: item.has_special_promotion || false,
          special_promotion_name: item.special_promotion_name || null,
          lineId,
        }];
      });
    },
    REMOVE_FROM_CART(state, lineId) {
      state.cart = applyCartChange(state.cart, (cart) =>
        cart.filter(item => item.lineId !== lineId)
      );
    },
    UPDATE_QUANTITY(state, { lineId, quantity }) {
      state.cart = applyCartChange(state.cart, (cart) => {
        const productIndex = cart.findIndex(item => item.lineId === lineId);
        if (productIndex === -1) return cart;

        if (quantity === 0) {
          return cart.filter(item => item.lineId !== lineId);
        }

        const validQuantity = isNaN(quantity) ? cart[productIndex].quantity : quantity;
        const finalQuantity = Math.max(1, validQuantity);
        const updated = [...cart];
        updated[productIndex] = { ...updated[productIndex], quantity: finalQuantity };
        return updated;
      });
    },
    CLEAR_CART(state) {
      state.cart = [];
      clearCartStorage();
    },
    SYNC_CART_FROM_STORAGE(state) {
      const stored = loadCartFromStorage();
      if (stored.length !== state.cart.length ||
          stored.some((item, i) => item.lineId !== state.cart[i]?.lineId || item.quantity !== state.cart[i]?.quantity)) {
        // localStorage representa el último cambio confirmado. Reemplazar evita
        // revivir líneas eliminadas o cantidades anteriores de otra pestaña.
        state.cart = stored;
      }
    },
    // Auth mutations
    SET_AUTH_USER(state, { token, username, isAdmin, userId, userEmail }) {
      state.auth.token = token;
      state.auth.username = username;
      state.auth.isAdmin = isAdmin;
      state.auth.userId = userId || null;
      state.auth.userEmail = userEmail || '';

      localStorage.setItem('accessToken', token);
      localStorage.setItem('username', username);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
      if (userId) localStorage.setItem('userId', userId);
      if (userEmail) localStorage.setItem('userEmail', userEmail);
    },
    CLEAR_AUTH(state) {
      state.auth.token = null;
      state.auth.username = '';
      state.auth.isAdmin = false;
      state.auth.userId = null;
      state.auth.userEmail = '';

      localStorage.removeItem('accessToken');
      localStorage.removeItem('username');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
    }
  },
  actions: {
    addToCart({ commit }, product) {
      commit('ADD_TO_CART', product);
    },
    removeFromCart({ commit }, lineId) {
      commit('REMOVE_FROM_CART', lineId);
    },
    updateQuantity({ commit }, payload) {
      commit('UPDATE_QUANTITY', payload);
    },
    clearCart({ commit }) {
      commit('CLEAR_CART');
    },

    syncCartFromStorage({ commit }) {
      commit('SYNC_CART_FROM_STORAGE');
    },

    setAuthUser({ commit }, userData) {
      commit('SET_AUTH_USER', userData);
    },
    logout({ commit }) {
      commit('CLEAR_AUTH');
    }
  }
});

export const initCrossTabCartSync = (store) => {
  window.addEventListener('storage', (event) => {
    if (event.key === CART_STORAGE_KEY) {
      store.dispatch('syncCartFromStorage');
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      store.dispatch('syncCartFromStorage');
    }
  });
};
